import { configure as configureAnalytics, tracker } from '@ixigo/analytics';
import { configure } from '@ixigo/fetch';
import { patchHistory } from '@repo/shared/navigation';
import { StartClient } from '@tanstack/react-start';
import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { getCookieHelpers } from '@/utils/cookie';
import {
	getCommonHeaders,
	getRequestContext,
	ixiUIDResolver,
} from '@/utils/headers';
import { handleUnauthorizedError } from './lib/login';
import { createRouter } from './router';
import { initGlobalAppTracker } from './utils/track';

function getClientId() {
	const url = new URL(window.location.href);
	return url.searchParams.get('clientId') || undefined;
}
async function initEnvironment() {
	patchHistory();
	const cookieStore = getCookieHelpers();
	const getIxiUID = ixiUIDResolver(cookieStore);

	// fire early to set the ixiUID cookie
	getIxiUID();

	const getClientConfig = () =>
		getRequestContext({
			clientId: getClientId(),
			getCookie: (key) => cookieStore.getCookie(key) || '',
			ua: navigator.userAgent,
		});
	const appConfig = getClientConfig();

	/**
	 * makes the fetch client available to the entire application
	 * with the given configuration
	 */
	configure({
		prefixUrl: import.meta.env.BASE_API_URL,
		hooks: {
			beforeRequest: async (request) => {
				await getIxiUID();
				const ctx = getCommonHeaders(
					getRequestContext({
						getCookie: (key) => cookieStore.getCookie(key) || '',
						ua: navigator.userAgent,
					}),
				);
				if (!ctx) return;
				Object.entries(ctx).forEach(([k, v]) => request.headers.set(k, v));
			},
			beforeError: async (httpError) => {
				return handleUnauthorizedError(httpError, { cookie: cookieStore });
			},
		},
	});

	configureAnalytics({
		clevertapProjectId: 'R5Z-849-WZ4Z',
		clientId: appConfig.clientId,
		// TODO: fix this type issue in "@ixigo/analytics"
		cookie: cookieStore as Parameters<typeof configureAnalytics>[0]['cookie'],
	});
	tracker.onEvent(({ data, name }) => {
		if (import.meta.env.PUBLIC_APP_ENV !== 'prod') {
			// Group related console logs
			console.group(
				`%c🔍 Analytics Event: ${name}`,
				'color: #0770E4; font-weight: bold;',
			);
			// Log event properties in a table if they exist
			if (data) {
				console.table(data);
			}
			console.groupEnd();
		}
	});
	initGlobalAppTracker();

	// Reuse server-injected theme CSS to avoid hydration mismatch, without recomputing on client
	const existingThemeCss =
		typeof document !== 'undefined'
			? document.getElementById('app-theme')?.textContent || undefined
			: undefined;
	return {
		clientConfig: getClientConfig,
		cookie: cookieStore,
		themeCss: existingThemeCss,
	};
}

initEnvironment().then((context) => {
	const router = createRouter(context);
	hydrateRoot(
		document,
		<StrictMode>
			<StartClient router={router} />
		</StrictMode>,
	);
});
